import { Switch, SwitchProps, theme } from 'antd';
import styled from 'styled-components';
import type { GlobalToken } from 'antd';
import { radius } from '../../theme/radius';

export type ToggleSize = 'm' | 'l';

export interface ToggleProps extends Omit<SwitchProps, 'size'> {
  /** Toggle size */
  size?: ToggleSize;
}

/** Pixel dimensions per size, keeping the handle inset from the track edges. */
const SIZE_DIMENSIONS: Record<
  ToggleSize,
  { minWidth: number; height: number; handle: number; offset: number }
> = {
  m: { minWidth: 32, height: 16, handle: 12, offset: 2 },
  l: { minWidth: 48, height: 24, handle: 20, offset: 2 },
};

interface StyledSwitchProps {
  $token: GlobalToken;
  $size: ToggleSize;
}

const StyledSwitch = styled(Switch)<StyledSwitchProps>`
  /* Base styles */
  min-width: ${({ $size }) => SIZE_DIMENSIONS[$size].minWidth}px;
  height: ${({ $size }) => SIZE_DIMENSIONS[$size].height}px;
  background: ${({ $token }) => $token.colorTextTertiary};

  /* Handle */
  .ant-switch-handle {
    width: ${({ $size }) => SIZE_DIMENSIONS[$size].handle}px;
    height: ${({ $size }) => SIZE_DIMENSIONS[$size].handle}px;
    top: ${({ $size }) => SIZE_DIMENSIONS[$size].offset}px;
    inset-inline-start: ${({ $size }) => SIZE_DIMENSIONS[$size].offset}px;

    &::before {
      border-radius: ${radius.circle};
      background: ${({ $token }) => $token.colorWhite};
    }
  }

  /* Checked state */
  &.ant-switch-checked {
    background: ${({ $token }) => $token.colorPrimary};

    .ant-switch-handle {
      inset-inline-start: calc(
        100% -
          ${({ $size }) =>
            SIZE_DIMENSIONS[$size].handle + SIZE_DIMENSIONS[$size].offset}px
      );
    }

    &:hover:not(.ant-switch-disabled) {
      background: ${({ $token }) => $token.colorPrimaryHover};
    }
  }

  /* Unchecked hover */
  &:hover:not(.ant-switch-disabled):not(.ant-switch-checked) {
    background: ${({ $token }) => $token.colorTextSecondary};
  }

  /* Disabled state */
  &.ant-switch-disabled {
    background: ${({ $token }) => $token.colorBgContainerDisabled};
    opacity: 1;

    .ant-switch-handle::before {
      background: ${({ $token }) => $token.colorTextDisabled};
    }
  }

  /* Focus */
  &:focus {
    box-shadow: none;
  }
`;

/**
 * Toggle component based on Figma Design System
 *
 * Now uses Ant Design theme tokens for consistent styling.
 *
 * @example
 * ```tsx
 * import { Toggle } from '@libertify/ui';
 *
 * <Toggle />
 * <Toggle defaultChecked />
 * <Toggle disabled />
 * <Toggle checked onChange={(checked) => console.log(checked)} />
 * ```
 */
export const Toggle = ({ size = 'm', ...props }: ToggleProps) => {
  const { token } = theme.useToken();
  return <StyledSwitch $token={token} $size={size} {...props} />;
};

export default Toggle;
